import { useState, useEffect } from "react";
import { auth, provider } from "../firebase-auth"; // Ensure correct Firebase config
import {
  signInWithEmailAndPassword, createUserWithEmailAndPassword,
  signInWithPopup, GoogleAuthProvider, sendEmailVerification, sendPasswordResetEmail,
  setPersistence, browserSessionPersistence
}
  from "firebase/auth";
import { useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import { API_URL } from "./constants";
import { useUserDetails } from "./UserDetailsContext";
import { TonalitySharp } from "@mui/icons-material";
import { Loader2 } from 'lucide-react';

// Whitelist of allowed redirect paths (security: prevent open redirect attacks)
const ALLOWED_REDIRECTS = [
  '/cdatasets'
  // '/pricing',
  // '/marketplace',
  // '/blog',
  // '/orders',
  // '/clean',
  // '/prompthistory',
  // '/creditinfo',
  // '/evalleader'
];

const Auth = ({ setUser, isModal, onSuccess }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isSignUp, setIsSignUp] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [verificationMessage, setVerificationMessage] = useState("");
  const navigate = useNavigate();
  const { userDetails, setUserDetails, token } = useUserDetails();

  // Capture referral code from URL query param and persist it for use after sign-up
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const ref = searchParams.get('ref');
    if (ref) {
      sessionStorage.setItem('referral_code', ref);
    }
  }, []);

  // Display any auth error passed back via sessionStorage (e.g. invalid referral code)
  useEffect(() => {
    const authError = sessionStorage.getItem('auth_error');
    if (authError) {
      setError(authError);
      sessionStorage.removeItem('auth_error');
    }
  }, []);

  const handleForgotPassword = async () => {
    if (!email) {
      setError("Please enter your email to reset password.");
      return;
    }
    try {
      await sendPasswordResetEmail(auth, email);
      setMessage("Password reset link sent! Check your email.");
    } catch (error) {
      console.error("Error sending password reset email:", error);
    }
  };



  // Handle Email/Password Auth
  const handleAuth = async (e) => {
    e.preventDefault();
    if (!agreed) {
      setError("Please read and agree to the Terms and Conditions!");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      if (isSignUp) {
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        const user = userCredential.user;
        await sendEmailVerification(user);
        await auth.signOut();
        if (setUser) setUser(null);
        setVerificationMessage("Account created successfully. Please check your email inbox and verify your email account.");
        setShowVerificationModal(true);
      } else {
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        const user = userCredential.user;

        if (!user.emailVerified) {
          await auth.signOut();
          if (setUser) setUser(null);
          setIsLoading(false);
          setVerificationMessage("Please verify your email address before login.");
          setShowVerificationModal(true);
          return;
        }

        if (setUser) setUser(user);

        // Background user management (non-blocking) - Handled by UserDetailsContext
        // manageUser(user) call removed

        // Check for returnTo parameter and redirect
        const searchParams = new URLSearchParams(window.location.search);
        const returnTo = searchParams.get('returnTo');
        if (isModal) {
          if (onSuccess) onSuccess();
        } else if (returnTo && ALLOWED_REDIRECTS.includes(returnTo)) {
          navigate(returnTo);
        } else {
          navigate('/');
        }
      }
    } catch (err) {
      console.error(err);
      setError("Something went wrong! Please check your credentials.");
      setIsLoading(false);
    }
  };

  const signInWithGoogle = async () => {
    if (!agreed) {
      setError("Please read and accept the Terms and Conditions.");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      await setPersistence(auth, browserSessionPersistence);
      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      if (setUser) setUser(user);

      // Background user management (non-blocking) - Handled by UserDetailsContext
      // manageUser(user) call removed

      // Check for returnTo parameter and redirect
      const searchParams = new URLSearchParams(window.location.search);
      const returnTo = searchParams.get('returnTo');
      if (isModal) {
        if (onSuccess) onSuccess();
      } else if (returnTo && ALLOWED_REDIRECTS.includes(returnTo)) {
        navigate(returnTo);
      } else {
        navigate('/');
      }
    } catch (error) {
      console.error(error);
      setIsLoading(false);
    }
  };


  return (
    <div className="flex flex-col items-center bg-white p-7 rounded-lg shadow-lg w-full max-w-lg">
      {/* <h2 className="text-3xl font-bold text-blue-600 mb-4">{isSignUp ? "Sign Up" : "Sign In"}</h2> */}

      {/* Social Login Buttons */}
      <div className="flex justify-center gap-4 w-full flex-wrap">
        {/* <button
            onClick={() => handleSocialSignIn(provider)}
            className="flex items-center justify-center w-[230px] bg-white border border-gray-300 p-2 rounded-lg hover:bg-gray-100 transition text-sm whitespace-nowrap"
        >
            <FcGoogle className="text-lg mr-2" /> Sign in with Google
        </button> */}
        {error && <p className="text-red-500 text-sm">{error}</p>}
        {message && <p className="text-green-500 text-sm">{message}</p>}
        <button
          className="w-full flex items-center justify-center gap-3 px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={isLoading}
          onClick={signInWithGoogle}
        >
          {isLoading ? (
            <Loader2 className="animate-spin w-6 h-6 text-blue-600" />
          ) : (
            <img src="google_logo.png" alt="Google" className="w-6 h-6" />
          )}
          {isLoading ? "Signing in..." : "Sign in with Google"}
        </button>
        {/* <button
            onClick={() => handleSocialSignIn(provider)}
            className="flex items-center justify-center w-[230px] bg-black text-white p-2 rounded-lg hover:bg-gray-800 transition text-sm whitespace-nowrap"
        >
            <FaGithub className="text-lg mr-2" /> Sign in with GitHub
        </button> */}
      </div>

      <div className="w-full border-t my-6"></div> {/* Divider */}

      {/* Email/Password Login */}
      <form onSubmit={handleAuth} className="w-full flex flex-col gap-4">
        <input
          type="email"
          placeholder="Enter your email"
          className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        <input
          type="password"
          placeholder="Enter your password"
          className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />

        {/* Terms and Conditions */}
        <div className="flex items-center gap-2">
          <input type="checkbox" checked={agreed} onChange={() => setAgreed(!agreed)} className="w-4 h-4" />
          <label className="text-gray-600 text-sm">
            I agree to the <a href="/terms-and-privacy" className="text-blue-500 hover:underline">Terms and Conditions</a>
          </label>
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          className="w-full bg-blue-600 text-white p-3 rounded-lg font-semibold hover:bg-blue-700 transition disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          disabled={isLoading}
        >
          {isLoading && <Loader2 className="animate-spin w-5 h-5" />}
          {isSignUp ? "Sign Up" : (isLoading ? "Signing in..." : "Sign In")}
        </button>
      </form>

      <p className="mt-4 text-gray-600 text-sm">
        {isSignUp ? "Already have an account?" : "Don't have an account?"}
        <button className="text-blue-500 font-semibold ml-1" onClick={() => setIsSignUp(!isSignUp)}>
          {isSignUp ? "Sign In" : "Sign Up"}
        </button>
      </p>
      <div className="forgot-password">
        <a href="#" className="text-blue-500 text-sm" onClick={handleForgotPassword}>Forgot Password?</a>
      </div>
      <ToastContainer position="top-center" autoClose={3000} />

      {/* Email Verification Modal */}
      {showVerificationModal && (
        <div className="fixed inset-0 flex items-center justify-center z-[100] bg-black bg-opacity-50 px-4">
          <div className="bg-white p-6 rounded-xl shadow-2xl max-w-sm w-full text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 mb-4">
               <svg className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
               </svg>
            </div>
            <h3 className="text-2xl font-bold mb-3 text-gray-800">Verify Your Email</h3>
            <p className="text-gray-600 mb-6">{verificationMessage}</p>
            <div className="flex flex-col gap-3">
              <button 
                onClick={() => {
                  window.location.href = "mailto:";
                }}
                className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition flex items-center justify-center gap-2"
              >
                Go to Email
              </button>
              <button 
                onClick={() => {
                  setShowVerificationModal(false);
                  navigate("/signin");
                }}
                className="w-full bg-gray-100 text-gray-800 py-3 rounded-lg font-semibold hover:bg-gray-200 transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Auth;